import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { AppService } from '../../core/app.service';
import { HoldProduct, SelectionHoldService } from '../../core/selection-hold.service';
import { loadSearchParams } from '../../core/search-url';
import { ConfirmService } from '../confirm/confirm.service';

/**
 * Funnel screens per product — the steps between picking a result and the booking being placed.
 *
 * <p>The ticket screen is NOT here: by then the booking exists and the hold is over.
 */
const FUNNEL_ROUTES: Record<HoldProduct, readonly string[]> = {
  flights: ['flights-passengers', 'flights-seats', 'flights-review'],
  trains: ['trains-passengers', 'trains-review'],
  buses: ['buses-passengers', 'buses-seats', 'buses-review'],
  hotels: ['hotels-rooms'],
};

/** Where "search again" and "back to search" send each product. */
const SEARCH_ROUTES: Record<HoldProduct, string> = {
  flights: 'flights-search',
  trains: 'trains-search',
  buses: 'buses-search',
  hotels: 'hotels-search',
};

const RESULTS_ROUTES: Record<HoldProduct, string> = {
  flights: 'flights-results',
  trains: 'trains-results',
  buses: 'buses-results',
  hotels: 'hotels-results',
};

/**
 * Drives the selection hold from navigation, and handles its expiry.
 *
 * <p>Renders nothing. Lives in the app shell so one instance covers every product — the
 * alternative was editing each product's "card selected" handler, which is four places to add it
 * and four to forget it when a fifth product arrives.
 *
 * <h2>Why navigation, not a click handler</h2>
 * Entering a funnel route IS the act of committing to a selection, for every product, however the
 * agent got there. Hooking the route means manual-entry and deep-link paths behave the same as
 * clicking a card, with no per-screen wiring.
 */
@Component({
  selector: 'app-selection-hold-host',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: '',
})
export class SelectionHoldHostComponent {
  private readonly router = inject(Router);
  private readonly hold = inject(SelectionHoldService);
  private readonly confirm = inject(ConfirmService);
  private readonly translate = inject(TranslateService);
  private readonly app = inject(AppService);
  private readonly destroyRef = inject(DestroyRef);

  /** Guards against a second dialog while the first is still open. */
  private handling = false;

  constructor() {
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd), takeUntilDestroyed(this.destroyRef))
      .subscribe((e) => this.onNavigated(e.urlAfterRedirects));

    // React to the clock reaching zero. An effect rather than a callback inside the service keeps
    // the service free of UI concerns.
    effect(() => {
      if (this.hold.expired()) void this.onExpired();
    });
  }

  /**
   * Manual entry is NOT held.
   *
   * <p>The countdown exists because a fare picked from a live search is only good for so long. A
   * manual booking has no such fare: the agent is typing up something already arranged out of
   * band, often from a phone call or a supplier email, and there is nothing upstream ageing. Running
   * a clock over it would be theatre — and worse, expiring it would throw away everything they had
   * typed and send them back to a search they never ran.
   *
   * <p>`manualMode` distinguishes the two: the manual flows set the bare product ('flight',
   * 'train', …) while search-and-book sets the '-online' variant. Hotels carry it on the URL
   * instead, as `?manual=1`, because that funnel is a routed page rather than a staged draft.
   */
  private isManualEntry(url: string): boolean {
    if (/[?&]manual=1(&|$)/.test(url)) return true;
    const mode = this.app.manualMode();
    return !!mode && !mode.endsWith('-online');
  }

  private onNavigated(url: string): void {
    const route = (url.split('?')[0] || '').replace(/^\//, '');

    // Nothing to hold, and nothing to expire. Drop any clock still running from a previous
    // search-and-book session so it cannot ambush a manual booking mid-typing.
    if (this.isManualEntry(url)) {
      if (this.hold.active()) this.hold.clear();
      return;
    }
    const entering = (Object.keys(FUNNEL_ROUTES) as HoldProduct[])
      .find((p) => FUNNEL_ROUTES[p].includes(route));

    if (entering) {
      // Idempotent for the same product, so stepping passengers → seats → payment does not keep
      // restarting the clock.
      this.hold.start(entering, 'funnel');
      return;
    }

    // The RESULTS list starts the clock too. The prices and availability on it came from a search
    // that is already ageing, so the window has to begin when the results appear — not when a fare
    // is picked, which can be many minutes later. Idempotent for the same product, so filtering
    // and sorting the list does not keep pushing the deadline out.
    const viewingResults = (Object.keys(RESULTS_ROUTES) as HoldProduct[])
      .find((p) => RESULTS_ROUTES[p] === route);
    if (viewingResults) {
        // A results list means a new search, which is exactly when holding again becomes
        // legitimate — so the seal left by a previous issue is lifted here rather than lingering
        // and silently disabling the hold for the rest of the session.
        this.hold.unseal();
      this.hold.start(viewingResults, 'results');
      return;
    }

    // Anywhere outside a funnel OR a results list — search, ticket, or an unrelated screen — the
    // hold no longer applies. Dropping it here is what stops an expiry dialog ambushing someone who has
    // long since moved on to a different part of the app.
    if (this.hold.active()) this.hold.clear();
  }

  private async onExpired(): Promise<void> {
    if (this.handling) return;
    this.handling = true;

    const product = this.hold.product();
    if (!product) { this.handling = false; return; }

    // Read the destinations BEFORE clearing, then clear — so the navigation below cannot be
    // mistaken for "left the funnel" and the dialog cannot reopen behind itself.
    const searchRoute = SEARCH_ROUTES[product];
    const resultsRoute = RESULTS_ROUTES[product];
    // Read the stage-dependent length BEFORE clearing — afterwards there is no stage to read.
    const minutes = this.hold.holdMinutes();
    // The criteria the results screen saved for this product. Carried onto the URL so the page the
    // agent lands on is addressable and reproducible, exactly as it was before the hold lapsed.
    const params = loadSearchParams(product) as Record<string, string> | null;
    this.hold.clear();

    const again = await this.confirm.ask({
      titleKey: 'common.hold.expiredTitle',
      messageKey: 'common.hold.expiredMessage',
      messageParams: { minutes },
      confirmKey: 'common.hold.searchAgain',
      cancelKey: 'common.hold.backToSearch',
      variant: 'danger',
    });

    if (!again) {
      // "Back to search" opens the form so the agent can change the criteria.
      this.app.navigate(searchRoute);
      this.handling = false;
      return;
    }

    // "Search again" has to produce NEW inventory, which routing alone never did: landing back on
    // the results list finds a still-populated search context and shows the same expired fares.
    //
    // Await the navigation first, THEN ask for the re-run. Order matters in both directions —
    // coming from the funnel the results screen is only constructed during the navigation, and it
    // has to exist before the request or nothing is listening; expiring while already on the list
    // makes this a same-URL navigation that resolves immediately and leaves the live screen in
    // place to receive it.
    await this.app.navigate(resultsRoute, params ?? undefined);
    this.hold.requestRefresh();
    this.handling = false;
  }
}
